import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Footer from "../components/Footer";
import Navbar from "../components/Navbar";
import Seo, { createWebPageSchema, getAbsoluteUrl } from "../components/Seo";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import { API_URL, getApiError } from "../lib/api";
import { getDevelopSlug } from "../lib/develop";

export default function Develop() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const description = "AI engineering, data engineering, and web development projects by Samuel Bagin, with implementation details and technical notes.";
  const pageSchema = useMemo(() => ({
    ...createWebPageSchema({
      path: "/portfolio/develop",
      name: "AI, Data & Web Development Projects | Samuel Bagin",
      description,
      type: "CollectionPage",
    }),
    mainEntity: {
      "@type": "ItemList",
      itemListElement: projects.map((project, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: project.title,
        url: getAbsoluteUrl(`/portfolio/develop/${getDevelopSlug(project)}`),
      })),
    },
  }), [description, projects]);

  useEffect(() => {
    const controller = new AbortController();

    fetch(`${API_URL}/api/develop`, { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error(await getApiError(response, "Failed to load development projects."));
        return response.json();
      })
      .then(setProjects)
      .catch((fetchError) => {
        if (fetchError.name !== "AbortError") setError(fetchError.message);
      })
      .finally(() => setLoading(false));

    return () => controller.abort();
  }, []);

  return (
    <div className="min-h-screen bg-[#111111] text-[#FEFEFA]">
      <Seo
        title="AI, Data & Web Development Projects | Samuel Bagin"
        description={description}
        path="/portfolio/develop"
        schema={pageSchema}
      />
      <Navbar />
      <main className="mx-auto min-h-screen max-w-6xl px-4 pb-24 pt-32 md:px-8">
        <header className="mb-12 max-w-3xl">
          <p className="mb-3 text-sm font-medium uppercase tracking-[0.2em] text-gray-300">AI, data, and web development</p>
          <h1 className="text-4xl font-semibold tracking-tight md:text-6xl">Engineering projects and technical work</h1>
          <p className="mt-5 text-lg leading-8 text-zinc-400">Open a project to read its full notes, implementation details, and links.</p>
        </header>

        {error && <Alert variant="destructive"><AlertTitle>Projects unavailable</AlertTitle><AlertDescription>{error}</AlertDescription></Alert>}

        {loading ? (
          <div className="grid gap-6 md:grid-cols-2">
            {[0, 1, 2, 3].map((item) => <Skeleton key={item} className="h-[430px]" />)}
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2">
            {projects.map((project) => (
              <Link key={project._id} to={`/portfolio/develop/${getDevelopSlug(project)}`} className="group block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-300">
                <Card className="flex h-full flex-col overflow-hidden border-zinc-800 bg-[#151515] transition duration-300 group-hover:-translate-y-1 group-hover:border-zinc-600">
                  <div className="relative h-64 overflow-hidden">
                    <img src={project.image} alt={project.title} className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                  </div>
                  <CardHeader>
                    <CardTitle className="text-2xl">{project.title}</CardTitle>
                  </CardHeader>
                  <CardContent className="flex flex-1 flex-col">
                    <CardDescription className="text-base leading-7">{project.shortText ?? ""}</CardDescription>
                    <span className="mt-auto self-end rounded-full border border-border bg-transparent px-4 py-2 text-sm font-semibold text-foreground transition group-hover:border-primary group-hover:bg-accent">
                      Get Details →
                    </span>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
